import clsx from "clsx";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import type { AIAssistantPageType, AIAssistantShareType } from "../model";
import { AIAssistantBottom } from "./ai-assistant-bottom";
import { ChatContent } from "./chat-content";
import { useAIAssistantChat } from "./hooks/use-ai-assistant-chat";
import { useIndividualProgramFlow } from "./hooks/use-individual-program-flow";
import { MainTitle } from "./main-title";

import s from "./ai-assistant.module.scss";

export type AIAssistantProps = ComponentProps<"div"> & {
  data: AIAssistantPageType & {
    sharedData: AIAssistantShareType | null;
  };
};

type Inputs = {
  userPrompt: string;
};

export const AIAssistant = (props: AIAssistantProps) => {
  const {
    data: { AITitles, defaultPrompts, sharedData, program_questions },
  } = props;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [hasValue, setHasValue] = useState(false);

  const {
    chatData,
    dialogueId,
    handleRefreshProgram,
    handleSetRating,
    hasRating,
    isLoading,
    setChatData,
    submitProgram,
    submitQuestion,
  } = useAIAssistantChat({
    sharedData,
    onQuestionSent: () => setHasValue(false),
  });

  const { register, handleSubmit, resetField } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    await submitQuestion(data.userPrompt);
    resetField("userPrompt");
  };

  const {
    individualStep,
    canGoBack,
    isLastStep,
    isNextDisabled,isSubmited,
    setSubquestionAnswer,
    subquestionData,
    handleBackToChat,
    handleBackToPrevStep,
    handleClickOnIndividual,
    handleGetProgram,
    handleNextQuestion,
  } = useIndividualProgramFlow({
    programQuestions: program_questions,
    setChatData,
    onSubmitProgram: submitProgram,
  });

  const handleClickOnPromt = useCallback(
    (text: string) => {
      onSubmit({ userPrompt: text });
    },
    [onSubmit],
  );

  const hasAnswer = useMemo(() => {
    return chatData.length > 0;
  }, [chatData]);

  const handleScrollToBottom = useCallback((sectionId: string | null) => {
    if (wrapperRef.current) {
      if (sectionId) {
        const targetSection = Array.from(
          wrapperRef.current.querySelectorAll<HTMLElement>("[id]"),
        ).find((element) => element.id === sectionId);

        if (targetSection) {
          wrapperRef.current.scrollTo({
            top: targetSection.offsetTop - 100,
            behavior: "smooth",
          });
          return;
        }
      }

      wrapperRef.current.scrollTo({
        top: wrapperRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  useEffect(() => {
    const lastAnswer = chatData.length > 0 && chatData[chatData.length - 1];
    const sectionId: string | null =
      lastAnswer && lastAnswer?.type === "answer" && "schedule" in lastAnswer
        ? (lastAnswer.key as string | null)
        : null;

    handleScrollToBottom(sectionId);
  }, [chatData]);

  return (
    <div className={clsx(s.root)}>
      <div className={clsx(s.container, "lenis-prevent")}>
        <div className={s.content} ref={wrapperRef}>
          {!hasAnswer && <MainTitle titles={AITitles} />}
          {hasAnswer && (
            <ChatContent
              chatData={chatData}
              hasRating={hasRating}
              dialogueId={dialogueId}
              individualStep={individualStep}
              programQuestions={program_questions}
              subquestionData={subquestionData}
              onSetRating={handleSetRating}
              onRefresh={handleClickOnPromt}
              scrollToBottom={handleScrollToBottom}
              onRefreshProgram={handleRefreshProgram}
              setSubquestionAnswer={setSubquestionAnswer}
            />
          )}
        </div>
        <AIAssistantBottom
          defaultPrompts={defaultPrompts}
          chatDataLength={chatData.length}
          hasAnswer={hasAnswer}
          hasValue={hasValue}
          isSubmited={isSubmited}
          individualStep={individualStep}
          canGoBack={canGoBack}
          isLastStep={isLastStep}
          isLoading={isLoading}
          isNextDisabled={isNextDisabled}
          register={register}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          onPromptClick={handleClickOnPromt}
          onClickOnIndividual={handleClickOnIndividual}
          onBackToChat={handleBackToChat}
          onBackToPrevStep={handleBackToPrevStep}
          onNextQuestion={handleNextQuestion}
          onGetProgram={handleGetProgram}
          onInputValueChange={setHasValue}
        />
      </div>
    </div>
  );
};

AIAssistant.displayName = "AIAssistant";
